import * as React from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useHubx } from "../../HubX";
import { FETCH_INSTALLATION_GROUPS_PAGINATED } from "../../Fedx";
import { Container, createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import { isArrayValid } from "../../helpers/utility";
import CardAdminInstallationGroup from "../../components/CardAdminInstallationGroup";
import { InstallationGroupAdmin } from "../../core/models/InstallationGroupAdmin";
import { EnrichedInstallationGroupPage } from "../../core/models/EnrichedInstallationGroupPage";
import { IEnumerable } from "../../core/models/ModelHelpers";
import { getPermissionFromEnum } from "../../core/models/Permission";
import { ordinationModel } from "../../core/models/BaseModels";
import {
  EVENT_ADMIN_FILTER_TEXT_CHANGE,
  EVENT_ADMIN_FILTER_ORDER_CHANGE,
  EVENT_ADMIN_FILTER_ORGANIZATION_CHANGE,
  EVENT_ADMIN_FILTER_DIRECTORSHIP_CHANGE,
  EVENT_ADMIN_FILTER_EXPIRED_GROUPS_CHANGE,
  EVENT_ADMIN_FILTER_MY_GROUPS,
} from "../../Events";
import { useHubXValue } from "../../core/hooks/useHubHooks";
import uniq from "lodash/uniq";
import { useHistory } from "react-router-dom";
import { InstallationGroupCardSkeleton } from "../../components/MaterialSkeleton";
import { useSnackbar } from "notistack";
import { Pagination } from "../../core/contracts/Pagination";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "background": theme.palette.background.default,

      "& .main-content": {
        padding: theme.spacing(2, 1),
      },
    },
  })
);

function Admin() {
  const [installationGroups, setInstallationGroups] = React.useState<InstallationGroupAdmin[]>([]);
  const [cardLoadingGroup, setCardLoadingGroup] = React.useState(new Array(6).fill({}));
  const [loading, setLoading] = React.useState(false);

  const history = useHistory();
  const hub = useHubx();
  const toastr = useSnackbar();

  const filterText = useHubXValue<string>(EVENT_ADMIN_FILTER_TEXT_CHANGE);
  const filterOrder = useHubXValue<ordinationModel>(EVENT_ADMIN_FILTER_ORDER_CHANGE, "asc");
  const filterOrganization = useHubXValue<string>(EVENT_ADMIN_FILTER_ORGANIZATION_CHANGE);
  const filterDirectorShip = useHubXValue<string>(EVENT_ADMIN_FILTER_DIRECTORSHIP_CHANGE);
  const filterExpiredGroup = useHubXValue<boolean>(EVENT_ADMIN_FILTER_EXPIRED_GROUPS_CHANGE, false);
  const filterMyGroups = useHubXValue<boolean>(EVENT_ADMIN_FILTER_MY_GROUPS, true);

  const [hasNextPage, setHasNextPage] = React.useState(false);
  const [pagination, setPagination] = React.useState<Pagination>({
    CurrentPage: 1,
    TotalPages: 0,
    PageSize: 10,
    TotalCount: 0,
  });

  React.useMemo(async () => {
    if (installationGroups) {
      setCardLoadingGroup(new Array(6).fill({}));
      setLoading(true);

      try {
        setInstallationGroups([]);
        const pageFilter: Pagination = {
          CurrentPage: 1,
          TotalPages: 0,
          PageSize: 10,
          TotalCount: 0,
          // Filter installation group by select (organization or directorship)
          // AND/OR by text field (name, organization or directorship)
          Filters: {
            TextInput: filterText,
            OrderBy: filterOrder,
            Organization: filterOrganization,
            Directorship: filterDirectorShip,
            ExpiredGroup: filterExpiredGroup,
            MyGroups: filterMyGroups,
          },
        };
        const APIResponse: EnrichedInstallationGroupPage = await hub.request(
          FETCH_INSTALLATION_GROUPS_PAGINATED,
          pageFilter
        );

        if (isArrayValid(APIResponse.items)) {
          setInstallationGroups(APIResponse.items);
        }

        setPagination(APIResponse.pageInfo);
        setHasNextPage(APIResponse.pageInfo.CurrentPage < APIResponse.pageInfo.TotalPages);
        return APIResponse.items;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Admin -> fetchInstallationGroups → error", error);
        toastr.enqueueSnackbar(
          "Ops! Ocorreu um erro ao tentar obter os grupos de instalação! Tente novamente mais tarde.",
          { variant: "error" }
        );
      } finally {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterText,
    filterOrder,
    filterOrganization,
    filterDirectorShip,
    filterExpiredGroup,
    filterMyGroups,
  ]);

  function handleClickEdit(id: IEnumerable) {
    history.push(`/admin/edit/${id}`);
  }

  const classes = useStyles();

  const onLoadMore = async () => {
    setCardLoadingGroup(new Array(1).fill({}));
    setLoading(true);
    const page = { ...pagination, CurrentPage: pagination.CurrentPage + 1 };

    const pageFilter: Pagination = {
      ...page,
      Filters: {
        TextInput: filterText,
        OrderBy: filterOrder,
        Organization: filterOrganization,
        Directorship: filterDirectorShip,
        ExpiredGroup: filterExpiredGroup,
        MyGroups: filterMyGroups,
      },
    };

    const result: EnrichedInstallationGroupPage = await hub.request(
      FETCH_INSTALLATION_GROUPS_PAGINATED,
      pageFilter
    );

    if (isArrayValid(result?.items)) {
      const { items, pageInfo } = result;
      const newGroupList = [...installationGroups, ...items];
      setInstallationGroups(newGroupList);
      setPagination(pageInfo);
    }
    setHasNextPage(page.CurrentPage < page.TotalPages);
    setLoading(false);
  };

  const infiniteRef = useInfiniteScroll({
    loading: loading,
    hasNextPage: hasNextPage,
    onLoadMore,
    scrollContainer: "window",
  });

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Grid
        container
        spacing={2}
        className="main-content"
        ref={infiniteRef as React.RefObject<HTMLTableSectionElement>}
      >
        {installationGroups &&
          installationGroups?.map((customer) => (
            <Grid item xs={4} key={customer.id}>
              <CardAdminInstallationGroup
                title={customer.name}
                groupType={customer.groupType}
                handleClick={(_) => handleClickEdit(customer.id)}
                organization={customer.organization}
                directorShip={customer.directorShip}
                numberOfActiveUsers={customer?.userListCount}
                numberOfActiveInstallations={customer?.installationListCount}
                inclusionDate={customer?.createDate ? new Date(customer?.createDate) : undefined}
                expirationDate={
                  customer?.expirationDate ? new Date(customer?.expirationDate) : undefined
                }
                permissions={uniq(
                  customer.permissionList?.map((permission) =>
                    getPermissionFromEnum(permission.type)
                  )
                )}
              />
            </Grid>
          ))}
        {loading &&
          cardLoadingGroup.map((_, idx) => (
            <Grid item xs={4} key={`loading-admin-card-${idx}`}>
              <InstallationGroupCardSkeleton />
            </Grid>
          ))}
      </Grid>
    </Container>
  );
}

export default Admin;
